import type { Ref } from "react";
import { styled } from "@pigment-css/react";
import { baseTheme } from "@acto/web-common/theme";
import type {
  TrendBarChartProps,
  TrendLineSeries,
} from "@acto/taskpane/field-sales-2/components/primitives/TrendBarChart";
import { hasTrend } from "@acto/taskpane/field-sales-2/components/primitives/TrendPercent";
import type { ComparisonLegendRow } from "./ComparisonLegend";
import { KpiCardShell, type KpiCardTrend } from "./KpiCardShell";
import { KpiFigure, type KpiFigureContent } from "./KpiFigure";

const Figures = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  columnGap: theme.fieldSalesTwo.spacing[40],
}));

/** The figures and legend for one span: the whole period or one focused bar. */
export interface KpiPairView {
  /** The bar KPI first, then the line KPI. */
  figures: readonly [KpiFigureContent, KpiFigureContent];
  legend: ComparisonLegendRow[];
}

export interface KpiPairTrend extends KpiCardTrend<KpiPairView> {
  chart: TrendBarChartProps & { lineSeries: TrendLineSeries };
}

export interface KpiPairCardProps {
  /** E.g. the customer name. */
  subtitle: string;
  title: string;
  trend: KpiPairTrend;
  figuresRef?: Ref<HTMLDivElement>;
}

/** Two KPIs of one period side by side, the first drawn as bars and the second as a line on the chart's right axis. */
export function KpiPairCard({ subtitle, title, trend, figuresRef }: KpiPairCardProps) {
  return (
    <KpiCardShell
      subtitle={subtitle}
      title={title}
      trend={trend}
      renderFigures={({ figures: [barFigure, lineFigure] }) => (
        <Figures ref={figuresRef}>
          <KpiFigure
            {...barFigure}
            seriesColor={baseTheme.fieldSalesTwo.colors.charts.primary}
            reserveTrendRow={hasTrend(lineFigure.trendPercent)}
          />
          <KpiFigure
            {...lineFigure}
            seriesColor={baseTheme.fieldSalesTwo.colors.charts.linePrimary}
            reserveTrendRow={hasTrend(barFigure.trendPercent)}
          />
        </Figures>
      )}
    />
  );
}
